// Iter152 · Split extraído de OfertasComercial.jsx (líneas 1941–2155).
// Contiene KPIsYTD y sus helpers internos (KpiBlock, Kpi, ImportKpi, HistoricTable).

import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import { Loader2, FileDown, FileSpreadsheet, Calculator, ChevronDown, ChevronUp, Info } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { useDGFilter } from "@/components/DGFilter";
import { fmtPct } from "./shared";
import { MetricInfo } from "@/components/MetricInfo";

function KPIsYTD() {
  const { user } = useAuth();
  const { dg: globalDG } = useDGFilter(user?.email);
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dgFilter, setDgFilter] = useState("all");  // all | SERVICIOS | TRATAMIENTO
  // Iter140 · sincroniza el pill DG global con el dropdown local
  useEffect(() => {
    if (globalDG && globalDG !== dgFilter) setDgFilter(globalDG);
    if (!globalDG && dgFilter !== "all") setDgFilter("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalDG]);
  const [historic, setHistoric] = useState([]);
  const [detailed, setDetailed] = useState([]);
  const [commercial, setCommercial] = useState(null);
  const [bizReviewUS, setBizReviewUS] = useState(null);
  const [bizReviewWT, setBizReviewWT] = useState(null);
  const [showHitDetail, setShowHitDetail] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const dgParam = dgFilter && dgFilter !== "all" ? `&direccion_general=${dgFilter}` : "";
      const res = await axios.get(`${API}/ofertas-comercial/kpis/ytd/export?year=${year}${dgParam}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `kpis_ytd_${year}_${dgFilter !== "all" ? dgFilter : "consolidado"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel YTD generado");
    } catch (e) {
      toast.error("No se pudo generar el Excel");
    } finally { setExporting(false); }
  };

  useEffect(() => {
    const dgParam = dgFilter && dgFilter !== "all" ? `&direccion_general=${dgFilter}` : "";
    axios.get(`${API}/ofertas-comercial/kpis/ytd?year=${year}${dgParam}`).then(r => setData(r.data));
    axios.get(`${API}/ofertas-comercial/kpis/historic`).then(r => setHistoric(r.data || []));
    axios.get(`${API}/ofertas-comercial/kpis/ytd-detailed?year=${year}`).then(r => setDetailed(r.data || [])).catch(() => {});
    axios.get(`${API}/ofertas-comercial/commercial-status?year=${year}`).then(r => setCommercial(r.data || null)).catch(() => {});
    // Iter283 · KPIs comerciales US/WT estilo Business Review (p.104/106)
    Promise.all([
      axios.get(`${API}/ytd-board/commercial-activity?dg=SERVICIOS&year=${year}`),
      axios.get(`${API}/ofertas-comercial/commercial-status?year=${year}&dg=SERVICIOS`),
    ]).then(([act, st]) => setBizReviewUS({ activity: act.data, status: st.data })).catch(() => {});
    Promise.all([
      axios.get(`${API}/ytd-board/commercial-activity?dg=TRATAMIENTO&year=${year}`),
      axios.get(`${API}/ofertas-comercial/commercial-status?year=${year}&dg=TRATAMIENTO`),
    ]).then(([act, st]) => setBizReviewWT({ activity: act.data, status: st.data })).catch(() => {});
  }, [year, dgFilter]);

  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14}/> Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="kpis-ytd">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs text-gray-600 font-semibold">Año:</label>
        <input type="number" value={year} onChange={e=>setYear(parseInt(e.target.value))} className="border border-gray-200 rounded px-3 py-1.5 text-xs w-24"/>
        {dgFilter !== "all" && (
          <span className="text-[10px] text-gray-500 italic">
            Filtro DG global activo: <b className="text-gray-700">{dgFilter}</b> — se aplica sobre KPIs, ratios, actividad y zona. Los bloques inferiores son globales.
          </span>
        )}
        <button
          onClick={handleExportExcel}
          disabled={exporting}
          data-testid="kpis-export-excel-btn"
          title="Descargar la foto YTD (Presentadas, Submission, Hit y desglose por actividad) en Excel para el comité"
          className="ml-auto text-xs bg-[#007934] hover:bg-[#0a5d2f] text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 font-semibold disabled:opacity-40"
        >
          {exporting ? <Loader2 className="animate-spin" size={13}/> : <FileSpreadsheet size={13}/>}
          Descargar Excel (comité)
        </button>
      </div>

      {/* Iter193 · Los KPI YTD ahora muestran número grande arriba y
          M€ abajo (mismo layout que Licitaciones). Cada tarjeta unifica
          count + importe en una sola. */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Total licitaciones" val={data.totales.total}      importe={data.totales.volumen_total_mm}/>
        <Kpi label="Presentadas"        val={data.totales.presentadas} importe={data.totales.volumen_presentadas_mm}/>
        <Kpi label="No presentadas"     val={data.totales.no_presentadas} importe={data.totales.volumen_no_presentadas_mm ?? 0}
          sub={data.totales.no_estrategicas ? `+${data.totales.no_estrategicas} no estratégicas (fuera del cálculo)` : undefined}/>
        <Kpi label="Adjudicadas VSM"    val={data.totales.adjudicadas} importe={data.totales.volumen_adjudicadas_mm ?? 0}/>
        <Kpi label="Pendientes"         val={data.totales.pendientes}  importe={data.totales.volumen_pendientes_mm ?? 0}/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
        <Kpi label="Submission rate"   val={fmtPct(data.ratios.submission_rate_vol)} sub={`Por nº: ${fmtPct(data.ratios.submission_rate_num)}`}
          info={<MetricInfo testId="metric-info-submission-rate" nombre="Submission rate"
            formula="Importe presentado / (Importe presentado + Importe no presentado) · variante por nº como secundario"
            poblacion="Ofertas del año por Fecha de Presentación (mismo criterio que la pestaña MERCADO). 'No presentadas' EXCLUYE las no estratégicas."
            unidad="% por importe (principal) · % por nº (secundario)"
            corte={`Año ${year}${dgFilter !== "all" ? ` · DG ${dgFilter}` : ""}`}/>}/>
        <Kpi label="Hit rate"          val={fmtPct(data.ratios.hit_rate_vol)} sub={`Por nº: ${fmtPct(data.ratios.hit_rate_num)} · Firmadas ${data.resueltas_ytd?.firmadas ?? 0} / No adj. ${data.resueltas_ytd?.no_adjudicadas ?? 0}`}
          info={<MetricInfo testId="metric-info-hit-rate-ytd" nombre="Hit rate"
            formula="Importe firmado / (Importe firmado + Importe no adjudicado) · 'No adjudicadas' = NO_ADJUDICADA + Adjudicada a otros · variante por nº como secundario"
            poblacion="Lo RESUELTO en el año por fecha de firma/adjudicación — incluye ofertas presentadas antes del año que seguían sin resolver a 1-ene"
            unidad="% por importe (principal) · % por nº (secundario)"
            corte={`Año ${year}${dgFilter !== "all" ? ` · DG ${dgFilter}` : ""}`}/>}/>
      </div>

      {/* David 2026-06 · Nota del "salto" del Hit rate: por importe suele ser
          menor que por nº cuando se firman muchas ofertas pequeñas pero las no
          adjudicadas incluyen pocas de gran importe que pesan en el total €. */}
      {(() => {
        const vol = data.ratios.hit_rate_vol, num = data.ratios.hit_rate_num;
        if (vol == null || num == null || (num - vol) < 0.08) return null;
        return (
          <div data-testid="hit-rate-gap-note" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
            <Info size={15} className="mt-0.5 shrink-0 text-amber-600"/>
            <span>
              El <b>Hit rate por importe ({fmtPct(vol)})</b> es menor que <b>por nº ({fmtPct(num)})</b> porque
              se han firmado muchas ofertas de <b>importe pequeño</b>, mientras que entre las no adjudicadas hay
              unas pocas de <b>gran importe</b> que pesan mucho más en el total de euros. En nº se ganan la mayoría;
              en € pesan más las grandes que no entraron. El dato que mira el comité es el de <b>importe</b>.
            </span>
          </div>
        );
      })()}

      {/* Iter411 · Puente Hit rate — detalle expediente a expediente de lo resuelto */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => setShowHitDetail(v => !v)}
          data-testid="hit-rate-detail-toggle"
          className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
        >
          <span className="flex items-center gap-2">
            <Calculator size={14} className="text-[#007934]"/>
            Cómo se calcula el Hit rate — {data.resueltas_ytd?.firmadas ?? 0} firmadas / {data.resueltas_ytd?.no_adjudicadas ?? 0} no adjudicadas en {year}
          </span>
          {showHitDetail ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
        </button>
        {showHitDetail && (
          <div className="border-t border-gray-100 p-4 space-y-4" data-testid="hit-rate-detail-panel">
            <p className="text-[11px] text-gray-500 leading-relaxed">
              <b>Hit rate = Firmadas / (Firmadas + No adjudicadas)</b> resueltas en {year}.
              Las filas marcadas con <span className="inline-block bg-amber-100 text-amber-700 rounded px-1 font-semibold">Arrastre</span> son
              ofertas presentadas antes de {year} que se resolvieron este año — por eso cuentan en el Hit rate aunque no estén en las Presentadas {year}.
            </p>
            <HitRateList title="Firmadas" items={data.resueltas_ytd?.firmadas_items || []} tone="green"/>
            <HitRateList title="No adjudicadas" items={data.resueltas_ytd?.no_adjudicadas_items || []} tone="rose"/>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Motivos no presentación</h3>
          {Object.entries(data.motivos_no_presentacion || {}).map(([m, n]) => (
            <div key={m} className="mb-2">
              <div className="flex items-center justify-between text-[11px] mb-0.5"><span>{m}</span><span className="font-bold">{n}</span></div>
              <div className="w-full bg-gray-100 rounded-full h-1.5"><div className="bg-rose-500 h-full rounded-full" style={{width:`${Math.min(100, (n/data.totales.no_presentadas)*100)}%`}}/></div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Desglose por actividad</h3>
          <table className="w-full text-xs">
            <thead><tr className="text-[10px] uppercase text-gray-500">
              <th className="text-left">Actividad</th><th className="text-right">Total</th><th className="text-right">Pres.</th><th className="text-right">Adj.</th><th className="text-right">M€</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {Object.entries(data.por_actividad).map(([a, s]) => (
                <tr key={a}><td className="py-1.5 font-semibold">{a}</td><td className="text-right">{s.total}</td><td className="text-right">{s.presentadas}</td><td className="text-right text-green-600 font-bold">{s.adjudicadas}</td><td className="text-right font-mono">{s.volumen_mm.toFixed(1)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Desglose por zona</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(data.por_zona).map(([z, s]) => (
            <div key={z} className="text-xs border border-gray-100 rounded-lg p-2.5">
              <div className="font-bold text-gray-900">{z}</div>
              <div className="text-gray-500 text-[10px] mt-0.5">{s.total} ofertas · {s.presentadas} presentadas</div>
              <div className="font-mono font-bold text-[#007934] mt-1">{s.volumen_mm.toFixed(2)} M€</div>
            </div>
          ))}
        </div>
      </div>

      {historic.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 overflow-x-auto">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Histórico (2017 – YTD) — Volumen publicado / presentado (M€) y % addressable</h3>
          <HistoricTable rows={historic}/>
        </div>
      )}

      {detailed.length > 0 && (
        <div className="bg-gradient-to-br from-[#007934]/5 to-transparent border border-[#007934]/20 rounded-xl p-4">
          <h3 className="text-xs font-semibold text-[#0a5d2f] uppercase tracking-wide mb-1">KPIs Cierre Excel (fuente oficial)</h3>
          <p className="text-[10px] text-[#0a5d2f]/70 mb-3">Regla Excel (comité) · sólo Servicios Urbanos · Nuevos + Renovaciones · valorado con ofertado %UTE</p>
          <div className="space-y-4">
            {detailed.map(b => <KpiBlock key={b.id} block={b}/>)}
          </div>
        </div>
      )}

      {commercial?.signed_status_valoriza && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 overflow-x-auto">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Commercial Status YTD — Signed vs Mercado</h3>
          <p className="text-[10px] text-gray-400 mb-3">Regla plataforma (histórico) · todas las DG y tipos de contrato</p>
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wide">
              <tr><th className="text-left p-2">Tipo</th><th className="text-right p-2">Valoriza (M€)</th><th className="text-right p-2">Addressable Signed (M€)</th><th className="text-right p-2">Hit Rate</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {commercial.signed_status_valoriza.map((r, i) => (
                <tr key={i} className={i === commercial.signed_status_valoriza.length - 1 ? "font-bold bg-[#007934]/5" : ""}>
                  <td className="p-2">{r.label}</td>
                  <td className="p-2 text-right font-mono text-[#007934]">{(r.valoriza_mm || 0).toFixed(2)}</td>
                  <td className="p-2 text-right font-mono">{(r.addressable_signed_mm || 0).toFixed(2)}</td>
                  <td className="p-2 text-right font-mono">{((r.hit_rate_pct || 0) * 100).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          {commercial.by_business_unit?.length > 0 && (
            <>
              <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mt-4 mb-2">Breakdown por BU</h4>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wide">
                  <tr>
                    <th className="text-left p-2"></th>
                    <th className="text-right p-2">Total (M€)</th>
                    <th className="text-right p-2">C&amp;W</th>
                    <th className="text-right p-2">GA</th>
                    <th className="text-right p-2">Mob</th>
                    <th className="text-right p-2">Others</th>
                    <th className="text-right p-2">Tratam.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {commercial.by_business_unit.map((r, i) => (
                    <tr key={i}>
                      <td className="p-2 font-semibold">{r.label}</td>
                      <td className="p-2 text-right font-mono">{(r.total_mm || 0).toFixed(2)}</td>
                      <td className="p-2 text-right font-mono text-gray-600">{(r.cw || 0).toFixed(2)}</td>
                      <td className="p-2 text-right font-mono text-gray-600">{(r.ga || 0).toFixed(2)}</td>
                      <td className="p-2 text-right font-mono text-gray-600">{(r.mob || 0).toFixed(2)}</td>
                      <td className="p-2 text-right font-mono text-gray-600">{(r.others || 0).toFixed(2)}</td>
                      <td className="p-2 text-right font-mono text-gray-600">{(r.tratamiento || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Iter283 · KPIs comerciales US vs WT, estilo Business Review p.104/106 */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Commercial Activity YTD — US vs WT (Business Review style)</h3>
          <button
            onClick={() => exportBusinessReviewPDF(bizReviewUS, bizReviewWT, year)}
            disabled={!bizReviewUS || !bizReviewWT}
            data-testid="bizreview-export-pdf-btn"
            title="Exportar paneles US/WT a PDF para el comité"
            className="text-xs bg-gray-800 hover:bg-black text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 font-semibold disabled:opacity-40"
          >
            <FileDown size={13} /> Exportar a PDF
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <BusinessReviewPanel label="US · Servicios Urbanos" dgData={bizReviewUS} accent="#007934" />
          <BusinessReviewPanel label="WT · Tratamiento" dgData={bizReviewWT} accent="#1d4ed8" />
        </div>
      </div>
    </div>
  );
}

// ── Iter283 · Exportación a PDF de los paneles US/WT para el comité ─────────
const WATERFALL_ROWS = [
  { key: "submitted", label: "Submitted" },
  { key: "first_position", label: "1st Position" },
  { key: "awarded", label: "Awarded" },
  { key: "pending_resolution", label: "Pending Resolution" },
  { key: "signed", label: "Signed" },
  { key: "cancelled", label: "Cancelled bids" },
  { key: "lost_to_competitor", label: "Lost bids" },
];

function drawBizReviewSection(doc, dgData, label, accent, margin, W, yStart) {
  let y = yStart;
  const [r, g, b] = accent;
  doc.setFillColor(r, g, b); doc.roundedRect(margin, y, W - margin * 2, 7, 1.5, 1.5, "F");
  doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont("helvetica", "bold");
  doc.text(label, margin + 3, y + 5);
  doc.setFontSize(8); doc.setFont("helvetica", "normal");
  doc.text(`Total Pipeline: ${(dgData.activity?.total_pipeline_valoriza_mm || 0).toFixed(2)} M€`, W - margin - 3, y + 5, { align: "right" });
  y += 11;

  const w = dgData.activity?.waterfall || {};
  const maxVal = Math.max(1, ...WATERFALL_ROWS.map(ro => w[ro.key]?.value_mm || 0));
  const barX = margin + 38; const barW = W - margin * 2 - 38 - 34;
  WATERFALL_ROWS.forEach(ro => {
    const v = w[ro.key] || { value_mm: 0, volume: 0 };
    doc.setTextColor(90, 90, 90); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
    doc.text(ro.label, margin, y + 3);
    doc.setFillColor(235, 235, 235); doc.roundedRect(barX, y, barW, 4, 1, 1, "F");
    doc.setFillColor(r, g, b); doc.roundedRect(barX, y, Math.max(barW * (v.value_mm / maxVal), 1.5), 4, 1, 1, "F");
    doc.setTextColor(30, 30, 30); doc.setFont("helvetica", "bold");
    doc.text(`${v.value_mm.toFixed(2)} M€  (${v.volume})`, W - margin, y + 3, { align: "right" });
    y += 6.5;
  });
  y += 3;

  // Hit rate table
  const status = dgData.status?.signed_status_valoriza || [];
  const cols = [margin, margin + 55, margin + 95, margin + 135, W - margin];
  doc.setTextColor(140, 140, 140); doc.setFontSize(6.5); doc.setFont("helvetica", "bold");
  doc.text("HIT RATE", cols[0], y);
  doc.text("VALORIZA M€", cols[1], y, { align: "right" });
  doc.text("ADDRESSABLE M€", cols[2] + 18, y, { align: "right" });
  doc.text("%", cols[3], y, { align: "right" });
  y += 4;
  doc.setDrawColor(230, 230, 230); doc.line(margin, y - 1.5, W - margin, y - 1.5);
  status.forEach((row, i) => {
    const bold = i === status.length - 1;
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setTextColor(30, 30, 30); doc.setFontSize(7.5);
    doc.text(row.label, cols[0], y);
    doc.setTextColor(r, g, b);
    doc.text((row.valoriza_mm || 0).toFixed(2), cols[1], y, { align: "right" });
    doc.setTextColor(90, 90, 90);
    doc.text((row.addressable_signed_mm || 0).toFixed(2), cols[2] + 18, y, { align: "right" });
    doc.setTextColor(30, 30, 30);
    doc.text(`${((row.hit_rate_pct || 0) * 100).toFixed(1)}%`, cols[3], y, { align: "right" });
    y += 5;
  });
  return y + 6;
}

function exportBusinessReviewPDF(bizReviewUS, bizReviewWT, year) {
  if (!bizReviewUS || !bizReviewWT) return;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const margin = 16;
  let y = 0;

  doc.setFillColor(0, 0, 0); doc.rect(0, 0, W, 26, "F");
  doc.setTextColor(255, 255, 255); doc.setFontSize(14); doc.setFont("helvetica", "bold");
  doc.text("Valoriza · Commercial Activity YTD", margin, 12);
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text(`Año ${year} · US vs WT · Datos en vivo (misma fuente que Licitaciones)`, margin, 19);
  const date = new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
  doc.text(date, W - margin, 19, { align: "right" });
  y = 36;

  y = drawBizReviewSection(doc, bizReviewUS, "US · Servicios Urbanos", [0, 121, 52], margin, W, y);
  y = drawBizReviewSection(doc, bizReviewWT, "WT · Tratamiento", [29, 78, 216], margin, W, y);

  doc.setTextColor(160, 160, 160); doc.setFontSize(6.5); doc.setFont("helvetica", "italic");
  doc.text("Generado automáticamente desde Valoriza — mismos datos que la pestaña Licitaciones.", margin, 290);

  doc.save(`commercial-activity-us-wt-${year}.pdf`);
}

function BusinessReviewPanel({ label, dgData, accent }) {
  if (!dgData) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-2 text-xs text-gray-400">
        <Loader2 className="animate-spin" size={14} /> Cargando {label}…
      </div>
    );
  }
  const w = dgData.activity?.waterfall || {};
  const rows = [
    { key: "submitted", label: "Submitted" },
    { key: "first_position", label: "1st Position" },
    { key: "awarded", label: "Awarded" },
    { key: "pending_resolution", label: "Pending Resolution" },
    { key: "signed", label: "Signed" },
    { key: "cancelled", label: "Cancelled bids" },
    { key: "lost_to_competitor", label: "Lost bids" },
  ];
  const maxVal = Math.max(1, ...rows.map(r => w[r.key]?.value_mm || 0));
  const status = dgData.status?.signed_status_valoriza || [];
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`bizreview-panel-${label.split(" ")[0]}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold" style={{ color: accent }}>{label}</h4>
        <span className="text-[11px] font-mono text-gray-500">Total Pipeline: <b style={{ color: accent }}>{(dgData.activity?.total_pipeline_valoriza_mm || 0).toFixed(2)} M€</b></span>
      </div>

      <div className="space-y-1.5 mb-4">
        {rows.map(r => {
          const v = w[r.key] || { value_mm: 0, volume: 0 };
          return (
            <div key={r.key} className="flex items-center gap-2 text-[11px]">
              <span className="w-32 shrink-0 text-gray-500">{r.label}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-3.5 relative overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.max(2, (v.value_mm / maxVal) * 100)}%`, background: accent, opacity: 0.8 }} />
              </div>
              <span className="w-20 shrink-0 text-right font-mono font-semibold text-gray-700">{v.value_mm.toFixed(2)} M€</span>
              <span className="w-10 shrink-0 text-right font-mono text-gray-400">{v.volume}</span>
            </div>
          );
        })}
      </div>

      <table className="w-full text-[11px]">
        <thead className="text-gray-400 uppercase text-[9px]">
          <tr><th className="text-left py-1">Hit Rate</th><th className="text-right py-1">Valoriza M€</th><th className="text-right py-1">Addressable M€</th><th className="text-right py-1">%</th></tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {status.map((r, i) => (
            <tr key={i} className={i === status.length - 1 ? "font-bold" : ""}>
              <td className="py-1">{r.label}</td>
              <td className="py-1 text-right font-mono" style={{ color: accent }}>{(r.valoriza_mm || 0).toFixed(2)}</td>
              <td className="py-1 text-right font-mono text-gray-500">{(r.addressable_signed_mm || 0).toFixed(2)}</td>
              <td className="py-1 text-right font-mono">{((r.hit_rate_pct || 0) * 100).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KpiBlock({ block }) {
  const cats = block.categories || {};
  const rates = cats.rates || {};
  const cells = [
    { key: "non_strategic",     color: "bg-gray-100 text-gray-700" },
    { key: "no_go",             color: "bg-rose-100 text-rose-700" },
    { key: "pending_decision",  color: "bg-amber-100 text-amber-700" },
    { key: "go_submitted",      color: "bg-blue-100 text-blue-700" },
    { key: "lost_cancelled",    color: "bg-stone-100 text-stone-700" },
    { key: "pending_resolution",color: "bg-indigo-100 text-indigo-700" },
    { key: "won",               color: "bg-green-200 text-green-900" },
  ];
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold text-gray-900">{block.block_label}</h4>
        <div className="flex gap-2 text-[10px]">
          <span className="bg-[#007934]/10 text-[#007934] px-2 py-0.5 rounded font-semibold">Sub: {((rates.submission_value || 0) * 100).toFixed(1)}% val · {((rates.submission_volume || 0) * 100).toFixed(1)}% vol</span>
          <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-semibold">Hit: {((rates.hit_value || 0) * 100).toFixed(1)}% val · {((rates.hit_volume || 0) * 100).toFixed(1)}% vol</span>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-7 gap-1.5">
        {cells.map(c => {
          const d = cats[c.key] || {};
          return (
            <div key={c.key} className={`rounded p-2 ${c.color}`}>
              <div className="text-[9px] uppercase font-bold opacity-80 truncate">{d.label}</div>
              <div className="font-mono text-sm font-bold mt-1">{(d.value_mm || 0).toFixed(2)} M€</div>
              <div className="text-[10px] opacity-80">{d.volume || 0} ops</div>
              {d.signed_mm != null && <div className="text-[10px] mt-0.5 border-t border-current/20 pt-0.5">Signed: <span className="font-mono font-bold">{d.signed_mm.toFixed(2)}</span></div>}
              {d.awarded_mm != null && <div className="text-[10px]">Awarded: <span className="font-mono font-bold">{d.awarded_mm.toFixed(2)}</span></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// Iter152 · Kpi también es usado por UpcomingList (queda en OfertasComercial.jsx).
export { Kpi };
export default KPIsYTD;

function HistoricTable({ rows }) {
  const grouped = useMemo(() => {
    const out = {};
    for (const r of rows) {
      const k = r.category_label;
      (out[k] = out[k] || []).push(r);
    }
    // sort each by year
    for (const k in out) out[k].sort((a,b)=>String(a.year).localeCompare(String(b.year)));
    return out;
  }, [rows]);

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <div className="text-xs font-bold text-gray-800 mb-1">{cat}</div>
          <table className="w-full text-[11px]">
            <thead><tr className="text-[10px] uppercase text-gray-500">
              <th className="text-left py-1">Métrica</th>
              {items.map(i => <th key={i.id} className="text-right py-1 px-1.5">{i.year}</th>)}
            </tr></thead>
            <tbody>
              <tr><td className="py-1 text-gray-600">Published</td>{items.map(i => <td key={i.id} className="text-right font-mono px-1.5">{i.published_eur_mm.toFixed(1)}</td>)}</tr>
              <tr><td className="py-1 text-gray-600">Submitted</td>{items.map(i => <td key={i.id} className="text-right font-mono px-1.5">{i.submitted_eur_mm.toFixed(1)}</td>)}</tr>
              <tr><td className="py-1 text-gray-600">% Addressable</td>{items.map(i => <td key={i.id} className="text-right font-mono px-1.5 text-[#007934]">{(i.pct_addressable_market*100).toFixed(1)}%</td>)}</tr>
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function fmtResDate(v) {
  if (!v) return "—";
  const s = String(v).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}

function HitRateList({ title, items, tone }) {
  const toneCls = tone === "green" ? "text-[#007934]" : "text-rose-600";
  const dotCls = tone === "green" ? "bg-[#007934]" : "bg-rose-500";
  return (
    <div data-testid={`hit-rate-list-${tone}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`w-2 h-2 rounded-full ${dotCls}`}/>
        <h4 className={`text-xs font-bold ${toneCls}`}>{title}</h4>
        <span className="text-[10px] text-gray-400">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <p className="text-[11px] text-gray-400 italic">Sin registros resueltos en el año.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-[9px] uppercase text-gray-400 border-b border-gray-100">
                <th className="text-left py-1 pr-2">Expediente</th>
                <th className="text-left py-1 pr-2">Cliente</th>
                <th className="text-left py-1 pr-2">Objeto</th>
                <th className="text-left py-1 pr-2">F. resolución</th>
                <th className="text-right py-1 pl-2">M€</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map((it, i) => (
                <tr key={it.id || i} className="align-top">
                  <td className="py-1 pr-2 font-semibold text-gray-800 whitespace-nowrap">
                    {it.expediente}
                    {it.arrastre && <span className="ml-1 inline-block bg-amber-100 text-amber-700 rounded px-1 text-[9px] font-semibold align-middle">Arrastre</span>}
                  </td>
                  <td className="py-1 pr-2 text-gray-600">{it.cliente}</td>
                  <td className="py-1 pr-2 text-gray-500 max-w-[220px] truncate" title={it.objeto}>{it.objeto}</td>
                  <td className="py-1 pr-2 text-gray-500 whitespace-nowrap font-mono">{fmtResDate(it.fecha_resolucion)}</td>
                  <td className="py-1 pl-2 text-right font-mono text-gray-700">{(Number(it.importe_mm) || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, val, sub, importe, info }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3">
      <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide flex items-center gap-1">{label}{info}</div>
      <div className="text-2xl font-bold text-gray-900 font-mono leading-tight mt-1">{val}</div>
      {importe !== undefined && (
        <div className="text-[11px] font-mono text-gray-600 mt-0.5">{(Number(importe) || 0).toFixed(2)} M€</div>
      )}
      {sub && <div className="text-[11px] text-zinc-500 font-medium mt-0.5">{sub}</div>}
    </div>
  );
}
